import { z } from 'zod';

import {
  ReceptionDamageSeveritySchema,
  ReceptionDamageTypeSchema,
} from './reception-damage.schemas.js';

export const CustomerPortalChecklistItemSchema = z.object({
  categoryKey: z.string(),
  itemKey: z.string(),
  status: z.string().nullable(),
  quantity: z.number().int().nullable(),
});

export const CustomerPortalDamagePhotoSchema = z.object({
  id: z.uuid(),
  url: z.url(),
  mediaType: z.enum(['IMAGE', 'VIDEO']),
  /** BR-SO-220: false is unclassified, not Sem avaria. */
  classified: z.boolean(),
  damageType: ReceptionDamageTypeSchema.nullable(),
  severity: ReceptionDamageSeveritySchema.nullable(),
});

export const CustomerPortalDamageSchema = z.object({
  id: z.uuid(),
  damageType: z.string().nullable(),
  zone: z.string().nullable(),
  comment: z.string().nullable(),
  photos: z.array(CustomerPortalDamagePhotoSchema),
});

export const CustomerPortalQuoteItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  quantity: z.string(),
  totalCents: z.number().int(),
  approved: z.boolean(),
  rejected: z.boolean(),
});

export const CustomerPortalQuoteViewSchema = z.object({
  kind: z.literal('quote'),
  placeName: z.string(),
  customerName: z.string(),
  vehicleLabel: z.string(),
  expiresAt: z.string(),
  signed: z.boolean(),
  items: z.array(CustomerPortalQuoteItemSchema),
});

export const CustomerPortalServiceOrderViewSchema = z.object({
  kind: z.literal('service-order'),
  placeName: z.string(),
  customerName: z.string(),
  vehicleLabel: z.string(),
  expiresAt: z.string(),
  signed: z.boolean(),
  priorApprovedCents: z.number().int(),
  items: z.array(CustomerPortalQuoteItemSchema),
});

export const CustomerPortalCheckoutItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  totalCents: z.number().int(),
});

export const CustomerPortalCheckoutViewSchema = z.object({
  kind: z.literal('checkout'),
  placeName: z.string(),
  customerName: z.string(),
  vehicleLabel: z.string(),
  orderLabel: z.string(),
  expiresAt: z.string(),
  signed: z.boolean(),
  items: z.array(CustomerPortalCheckoutItemSchema),
  totalCents: z.number().int(),
  odometerKm: z.number().int().nullable(),
  fuelLevelEighths: z.number().int().nullable(),
  termText: z.string(),
});

export const IssueCheckoutPortalLinkResponseSchema = z.object({
  url: z.url(),
});

export const CustomerPortalReceptionViewSchema = z.object({
  kind: z.literal('reception'),
  placeName: z.string(),
  customerName: z.string(),
  vehicleLabel: z.string(),
  entryOdometerKm: z.number().int().nullable(),
  fuelLevelEighths: z.number().int().nullable(),
  reportedIssue: z.string().nullable(),
  termText: z.string().nullable(),
  expiresAt: z.string(),
  signed: z.boolean(),
  checklist: z.array(CustomerPortalChecklistItemSchema),
  damages: z.array(CustomerPortalDamageSchema),
});

export const CustomerPortalLinkViewSchema = z.discriminatedUnion('kind', [
  CustomerPortalQuoteViewSchema,
  CustomerPortalServiceOrderViewSchema,
  CustomerPortalReceptionViewSchema,
  CustomerPortalCheckoutViewSchema,
]);

export const SubmitCustomerPortalLinkBodySchema = z.object({
  signedByName: z.string().trim().min(2).max(120),
  signaturePngDataUrl: z.string().min(32).max(500_000),
  approvedItemIds: z.array(z.uuid()).default([]),
  rejectedItemIds: z.array(z.uuid()).default([]),
});

export const SubmitCustomerPortalLinkResponseSchema = z.object({
  signed: z.literal(true),
});

export type IssueCheckoutPortalLinkResponse = z.infer<typeof IssueCheckoutPortalLinkResponseSchema>;
export type CustomerPortalLinkView = z.infer<typeof CustomerPortalLinkViewSchema>;
export type SubmitCustomerPortalLinkBody = z.infer<typeof SubmitCustomerPortalLinkBodySchema>;
export type SubmitCustomerPortalLinkResponse = z.infer<
  typeof SubmitCustomerPortalLinkResponseSchema
>;
